import { RichTextRenderer } from '@/ui/rich-text-renderer';
import DOMPurify from 'dompurify';
import React from 'react';
import styled from 'styled-components';
import {
  extractTextFromHtml,
  getTextDifferences,
  HIGHLIGHT_CLASSES,
  HIGHLIGHT_STYLES,
  type HighlightSegment,
} from './utils/highlighting.utils';

const HtmlHighlightContainer = styled(RichTextRenderer)`
  /* Style highlighted spans within HTML content */
  .${HIGHLIGHT_CLASSES.added} {
    background-color: ${HIGHLIGHT_STYLES.added.backgroundColor} !important;
    color: ${HIGHLIGHT_STYLES.added.color};
    padding: ${HIGHLIGHT_STYLES.added.padding};
    border-radius: ${HIGHLIGHT_STYLES.added.borderRadius};
    box-decoration-break: ${HIGHLIGHT_STYLES.added.boxDecorationBreak};
    -webkit-box-decoration-break: ${HIGHLIGHT_STYLES.added
      .WebkitBoxDecorationBreak};
  }

  .${HIGHLIGHT_CLASSES.removed} {
    background-color: ${HIGHLIGHT_STYLES.removed.backgroundColor} !important;
    color: ${HIGHLIGHT_STYLES.removed.color};
    text-decoration: ${HIGHLIGHT_STYLES.removed.textDecoration};
    padding: ${HIGHLIGHT_STYLES.removed.padding};
    border-radius: ${HIGHLIGHT_STYLES.removed.borderRadius};
    box-decoration-break: ${HIGHLIGHT_STYLES.removed.boxDecorationBreak};
    -webkit-box-decoration-break: ${HIGHLIGHT_STYLES.removed
      .WebkitBoxDecorationBreak};
  }
`;

/**
 * Applies highlighting to HTML content based on text differences
 */
const applyHighlightingToHtml = (
  html: string,
  segments: HighlightSegment[],
): string => {
  // Create a temporary DOM element to work with
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = html;

  // Walk through text nodes and apply highlighting
  const walker = document.createTreeWalker(tempDiv, NodeFilter.SHOW_TEXT);

  const textNodes: Text[] = [];
  let node;
  while ((node = walker.nextNode())) {
    textNodes.push(node as Text);
  }

  // Apply highlighting to text nodes
  let segmentIndex = 0;
  let segmentOffset = 0;

  textNodes.forEach((textNode) => {
    const nodeText = textNode.textContent || '';
    let newNodeContent = '';
    let nodeOffset = 0;

    while (nodeOffset < nodeText.length && segmentIndex < segments.length) {
      const segment = segments[segmentIndex];
      const segmentText = segment.text;
      const remainingSegmentText = segmentText.substring(segmentOffset);
      const remainingNodeText = nodeText.substring(nodeOffset);

      if (remainingNodeText.startsWith(remainingSegmentText)) {
        // Full segment fits in this node
        if (segment.isChanged) {
          const className =
            segment.type === 'added'
              ? HIGHLIGHT_CLASSES.added
              : HIGHLIGHT_CLASSES.removed;
          newNodeContent += `<span class="${className}">${remainingSegmentText}</span>`;
        } else {
          newNodeContent += remainingSegmentText;
        }
        nodeOffset += remainingSegmentText.length;
        segmentIndex++;
        segmentOffset = 0;
      } else if (remainingSegmentText.startsWith(remainingNodeText)) {
        // Partial segment fits in this node
        if (segment.isChanged) {
          const className =
            segment.type === 'added'
              ? HIGHLIGHT_CLASSES.added
              : HIGHLIGHT_CLASSES.removed;
          newNodeContent += `<span class="${className}">${remainingNodeText}</span>`;
        } else {
          newNodeContent += remainingNodeText;
        }
        segmentOffset += remainingNodeText.length;
        nodeOffset = nodeText.length;
      } else {
        // Find common prefix
        let commonLength = 0;
        while (
          commonLength < remainingNodeText.length &&
          commonLength < remainingSegmentText.length &&
          remainingNodeText[commonLength] === remainingSegmentText[commonLength]
        ) {
          commonLength++;
        }

        if (commonLength > 0) {
          const commonText = remainingNodeText.substring(0, commonLength);
          if (segment.isChanged) {
            const className =
              segment.type === 'added'
                ? HIGHLIGHT_CLASSES.added
                : HIGHLIGHT_CLASSES.removed;
            newNodeContent += `<span class="${className}">${commonText}</span>`;
          } else {
            newNodeContent += commonText;
          }
          nodeOffset += commonLength;
          segmentOffset += commonLength;
        } else {
          // Recover from whitespace mismatches between segments and DOM.
          // extractTextFromHtml injects spaces between block elements and
          // collapses whitespace runs, so segments may carry whitespace the
          // DOM text nodes do not (or vice versa). Skip past it instead of
          // abandoning the whole segment.
          const segLeadingWsMatch = /^\s+/.exec(remainingSegmentText);
          const nodeLeadingWsMatch = /^\s+/.exec(remainingNodeText);

          if (segLeadingWsMatch && nodeLeadingWsMatch) {
            // Both sides lead with whitespace that differs only in form
            // (e.g. a normalized space in the segment vs `&nbsp;`/U+00A0 in
            // the DOM node). Consume one run on each side together —
            // preserving the node's original whitespace — instead of
            // abandoning the rest of the segment, which would desync the
            // replay and mis-highlight later text.
            newNodeContent += nodeLeadingWsMatch[0];
            nodeOffset += nodeLeadingWsMatch[0].length;
            const segWsLength = segLeadingWsMatch[0].length;
            if (segmentOffset + segWsLength >= segmentText.length) {
              segmentIndex++;
              segmentOffset = 0;
            } else {
              segmentOffset += segWsLength;
            }
          } else if (segLeadingWsMatch) {
            const wsLength = segLeadingWsMatch[0].length;
            if (segmentOffset + wsLength >= segmentText.length) {
              segmentIndex++;
              segmentOffset = 0;
            } else {
              segmentOffset += wsLength;
            }
          } else if (nodeLeadingWsMatch) {
            const wsLength = nodeLeadingWsMatch[0].length;
            newNodeContent += nodeLeadingWsMatch[0];
            nodeOffset += wsLength;
          } else {
            // No match, move to next segment
            segmentIndex++;
            segmentOffset = 0;
          }
        }
      }
    }

    // Add any remaining node text
    if (nodeOffset < nodeText.length) {
      newNodeContent += nodeText.substring(nodeOffset);
    }

    // Replace the text node with highlighted content
    if (newNodeContent !== nodeText) {
      const wrapper = document.createElement('span');
      wrapper.innerHTML = newNodeContent;
      textNode.parentNode?.replaceChild(wrapper, textNode);
    }
  });

  return tempDiv.innerHTML;
};

type HighlightedHtmlProps = {
  originalHtml: string;
  newHtml: string;
  className?: string;
};

/**
 * Component that highlights differences between original and new HTML content
 * Preserves HTML structure while highlighting text changes
 * Automatically sanitizes HTML content for security
 */
export const HighlightedHtml: React.FC<HighlightedHtmlProps> = ({
  originalHtml,
  newHtml,
  className,
}) => {
  // Extract plain text for comparison
  const originalPlainText = React.useMemo(() => {
    return extractTextFromHtml(originalHtml);
  }, [originalHtml]);

  const newPlainText = React.useMemo(() => {
    return extractTextFromHtml(newHtml);
  }, [newHtml]);

  // Get text differences based on plain text
  const segments = React.useMemo(() => {
    return getTextDifferences(originalPlainText, newPlainText);
  }, [originalPlainText, newPlainText]);

  // Apply highlighting to HTML content
  const highlightedHtml = applyHighlightingToHtml(newHtml, segments);
  const sanitizedHtml = DOMPurify.sanitize(highlightedHtml);

  return (
    <HtmlHighlightContainer content={sanitizedHtml} className={className} />
  );
};
